import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Kicker } from "@/components/form";
import { AREAS } from "@/data/site";

const IMG = {
  versicherung: "https://static.prod-images.emergentagent.com/jobs/9cdaf805-ebb2-4698-b869-6dae56934adb/images/784a8fc6a4a3317f8ca46320cb2ae408bf8b0dc1d807de730ac592a97ba36d4e.png",
  finanzierung: "https://static.prod-images.emergentagent.com/jobs/9cdaf805-ebb2-4698-b869-6dae56934adb/images/e7655b5b0ee46034992317edf36ccb562bc5768fd40b753b9c4ffbb52e44430c.png",
  immobilien: "https://static.prod-images.emergentagent.com/jobs/9cdaf805-ebb2-4698-b869-6dae56934adb/images/a293bc91c1302bbefd76e2891e601d8aa724a21ec770a01242b1787de1327752.png",
};

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

const QUOTE_LINKS = {
  versicherung: [["/anfrage/versicherung-privat", "Privat-Angebot"], ["/anfrage/versicherung-gewerbe", "Gewerbe-Angebot"], ["/schaden-melden", "Schaden melden"]],
  finanzierung: [["/anfrage/finanzierung-bau", "Baufinanzierung"], ["/anfrage/finanzierung-privat", "Privatkredit"], ["/anfrage/finanzierung-gewerbe", "Gewerbefinanzierung"]],
  immobilien: [["/immobilien", "Objekte ansehen"]],
};

export default function ServicePage({ areaKey }) {
  const { slug } = useParams();
  const area = AREAS.find((a) => a.key === areaKey || a.slug === slug);
  if (!area) return <Navigate to="/" replace />;
  const quotes = QUOTE_LINKS[area.key] || [];

  return (
    <div data-testid={`service-page-${area.key}`}>
      <Seo title={area.title} description={area.intro} path={`/${area.slug}`} keywords={area.keywords} ogImage={area.ogImage} />

      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG[area.key]} alt={area.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-900/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fade} className="max-w-3xl">
            <Kicker>{area.kicker}</Kicker>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-none">{area.title}</h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-2xl">{area.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {quotes.map(([to, label], i) => (
                <Link key={to} to={to} data-testid={`quote-link-${i}`} className={`rounded-sm px-6 py-3 text-sm font-bold transition-colors ${i === 0 ? "bg-gold-500 text-navy-900 hover:bg-gold-300" : "border border-white/20 text-white hover:border-gold-500 hover:text-gold-500"}`}>{label}</Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-navy-900 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {area.services.map((s, i) => (
              <motion.div key={s.title} {...fade} transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-navy-800 border border-white/5 rounded-md p-8 hover:border-gold-500/40 transition-colors duration-300">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-sm bg-gold-500/15 flex items-center justify-center"><Check size={16} className="text-gold-500" /></span>
                  <h3 className="text-xl font-bold text-white">{s.title}</h3>
                </div>
                <p className="mt-4 text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-r from-navy-800 to-navy-700 rounded-md p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8 border border-white/5">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{area.tagline}</h3>
              <p className="mt-3 text-slate-300">Sprechen Sie mit unseren Experten für {area.title}.</p>
            </div>
            <div className="flex gap-4 shrink-0">
              <Link to="/termin-buchen" className="bg-gold-500 text-navy-900 font-bold rounded-sm px-8 py-4 hover:bg-gold-300 transition-colors whitespace-nowrap">Termin buchen</Link>
              <Link to="/kontakt" className="group border border-white/20 text-white rounded-sm px-8 py-4 hover:border-gold-500 hover:text-gold-500 transition-colors flex items-center gap-2 whitespace-nowrap">Anfrage <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
